import React, { useEffect } from "react";
import axios from "axios";
import API_BASE_URL from "../../src/config";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { formatLocalDate } from "../utils/dateUtils";
import { Fancybox } from "@fancyapps/ui";
import "@fancyapps/ui/dist/fancybox/fancybox.css";

const PropertyRooms = ({ stayData, bestCombination, allCombinations, headingRef }) => {
  console.log("StayData:", stayData);
  const navigate = useNavigate();

  // Binds every room's images as their own Fancybox group (data-fancybox
  // scoped per room id below), same pattern as the main property Gallery —
  // a broad "starts with" selector so it covers every room across both the
  // Best Combination and any Other Combination boxes without needing to
  // re-bind per render.
  useEffect(() => {
    Fancybox.bind("[data-fancybox^='room-']", {
      Thumbs: { autoStart: false },
      Toolbar: { display: ["zoom", "slideshow", "thumbs", "close"] },
    });

    return () => {
      Fancybox.destroy();
    };
  }, [bestCombination, allCombinations]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-IN").format(price);
  };

const handleBookCombination = async (comboRooms, isAvailable) => {
  if (!isAvailable) {
    toast.error("Rooms not available");
    return;
  }

  try {
    let params = JSON.parse(localStorage.getItem("searchParams") || "{}");

    /*. DEFAULT VALUES IF NOT AVAILABLE */
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);

    if (!params.checkin_date || !params.checkout_date) {
      params = {
        ...params,
        checkin_date: formatLocalDate(today),
        checkout_date: formatLocalDate(tomorrow),
        adults: "1",
        kids: "0",
        rooms_required: "1",
      };
    }

    /*. SAFETY CHECK */
    if (!comboRooms || comboRooms.length === 0) {
      toast.error("No rooms selected");
      return;
    }

    /*. GROUP SAME ROOM IDS */
    const roomMap = {};

    comboRooms.forEach((room) => {
      if (roomMap[room.id]) {
        roomMap[room.id].room_count += 1;
      } else {
        roomMap[room.id] = {
          room_id: room.id,
          room_count: 1,
        };
      }
    });

    /*. FINAL PAYLOAD */
    const payload = {
      stay_id: stayData.id,
      selected_rooms: Object.values(roomMap),
      check_in: params.checkin_date,
      check_out: params.checkout_date,
      adults: Number(params.adults || 1),
      children: Number(params.kids || 0),
    };

    console.log("FINAL PAYLOAD:", payload);

    const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
    const token = storedUser.accessToken || "";

    const response = await axios.post(
      `${API_BASE_URL}bookings/review-combination-booking`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    console.log("API RESPONSE:", response?.data);

    if (response?.data?.success) {

      /*. FIXED STORAGE */
      localStorage.setItem(
        "draftBooking",
        JSON.stringify({
          success: true,
          stay: stayData,

          //. CORRECT DATA
          selected_rooms: Object.values(roomMap),
          rooms: comboRooms,

          summary: response.data.summary,

          checkIn: params.checkin_date,
          checkOut: params.checkout_date,

          adults: payload.adults,
          children: payload.children,
        })
      );

      navigate("/booking/review");
    } else {
      toast.error(response?.data?.message || "Booking failed");
    }

  } catch (err) {
    console.error("BOOKING ERROR:", err);
    toast.error("Something went wrong");
  }
};

  const parseBenefits = (room) => {
    try {
      return Array.isArray(room.benefits)
        ? room.benefits
        : JSON.parse(room.benefits || "[]");
    } catch {
      return [];
    }
  };

  const RoomCard = ({ room }) => {
    const benefits = parseBenefits(room);
    const isAvailable = room.available !== false;

    return (
      <div className="roomCard">

        {!isAvailable && (
          <div className="notAvailable">Not Available</div>
        )}

        <a
          href={room.cover_image || room.cover_image_url}
          data-fancybox={`room-${room.id}`}
          data-caption={room.title}
        >
          <img
            src={room.cover_image || room.cover_image_url}
            alt=""
            className="mainImg"
          />
        </a>

        {/* THUMB IMAGES */}
        {room.images?.length > 0 && (
          <div className="thumbRow">
            {room.images.slice(0, 3).map((img, i) => (
              <a
                key={i}
                href={img.image || img.image_url}
                data-fancybox={`room-${room.id}`}
                data-caption={room.title}
              >
                <img
                  src={img.image || img.image_url}
                  style={{height: "80px", width: "80px", objectFit: "cover", margin: "5px", borderRadius: "25px"}}
                  alt=""
                />
              </a>
            ))}
          </div>
        )}

        <div className="roomContent">
          <h4>{room.title}</h4>

          <div className="roomPrice">
            ₹{formatPrice(room.price_min)}
          </div>

          <ul>
            {benefits.length > 0 ? (
              benefits.map((b, i) => <li key={i}>✔ {b}</li>)
            ) : (
              <li>No benefits</li>
            )}
          </ul>
        </div>
      </div>
    );
  };

  const CombinationBox = ({ combo, title }) => {
    const isAvailable = combo.rooms.every(r => r.available !== false);

    return (
      <div className="comboWrapper">

        <h3 className="comboTitle">{title}</h3>

        <div className="comboBody">

          {/* LEFT SLIDER */}
          <div className="roomsSlider">
            {combo.rooms.map((room) => (
              <RoomCard key={room.id} room={room} />
            ))}
          </div>

          {/* RIGHT FIXED PANEL */}
          <div className="comboSummary">

            <div className="priceBox">
              <span className="price"> ₹ {formatPrice(combo.total_price)}</span> <span>/ per night</span>
            </div>

            {!isAvailable && (
              <div className="unavailableText">
                Some rooms not available
              </div>
            )}

            <button
              className={`bookBtn ${!isAvailable ? "disabled" : ""}`}
              onClick={() => handleBookCombination(combo.rooms, isAvailable)}
            >
              {isAvailable ? "Book Now" : "Unavailable"}
            </button>

          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="choose_room" ref={headingRef}>

      <h2 className="title">
        Choose Your Room
      </h2>

      {bestCombination && (
        <CombinationBox
          combo={bestCombination}
          title="Best Combination"
        />
      )}

      {allCombinations?.slice(1).map((combo, i) => (
        <CombinationBox
          key={i}
          combo={combo}
          title={`Other Combination ${i + 1}`}
        />
      ))}
    </div>
  );
};

export default PropertyRooms;



// import React, { useRef, useState, useEffect } from "react";
// import axios from "axios";
// import Calendar from "react-calendar";
// import "react-calendar/dist/Calendar.css";
// import API_BASE_URL from "../../src/config";
// import { toast } from "react-toastify";
// import { useNavigate } from "react-router-dom";

// const PropertyRooms = ({ rooms, stayData, headingRef }) => {
//   const navigate = useNavigate();
//   const roomsRef = useRef(null);
//   const [roomAvailability, setRoomAvailability] = useState({});
//   const [selectedDates, setSelectedDates] = useState({});
//   const [tempStartDate, setTempStartDate] = useState(null);
//   const [message, setMessage] = useState("");
//   const [roomUnavailableState, setRoomUnavailableState] = useState({});


//   //. Helper to parse YYYY-MM-DD as local date (avoid timezone shift)
//   const parseLocalDate = (dateStr) => {
//     if (!dateStr) return null;
//     const [year, month, day] = dateStr.split("-").map(Number);
//     return new Date(year, month - 1, day);
//   };



//   useEffect(() => {
//     const params = JSON.parse(localStorage.getItem("searchParams") || "{}");

//     if (params.checkin_date && params.checkout_date && rooms?.length) {
//       const start = parseLocalDate(params.checkin_date);
//       const end = parseLocalDate(params.checkout_date);
//       const preselect = {};
//       const roomUnavailable = {};

//       rooms.forEach((room, idx) => {
//         const roomId = room.id || room.title || idx;
//         const allAvailabilities = room.units?.flatMap((u) => u.availability || []) || [];

//         const dateRange = [];
//         let current = new Date(start);
//         while (current < end) {
//           dateRange.push(current.toLocaleDateString("en-CA"));
//           current.setDate(current.getDate() + 1);
//         }

//         //. Compute per-room availability across all units
//         const isFullyAvailable = dateRange.every((d) => {
//           const dayAvailabilities = allAvailabilities.filter((a) => a.date === d);

//           //. If no data → treat as unavailable
//           if (!dayAvailabilities.length) return false;

//           return dayAvailabilities.some(
//             (a) => a.is_available === true && parseInt(a.rooms_available || 0) > 0
//           );
//         });


//         if (isFullyAvailable) {
//           preselect[roomId] = [start, end];
//           roomUnavailable[roomId] = false;
//         } else {
//           roomUnavailable[roomId] = true;
//         }
//       });

//       setSelectedDates(preselect);
//       setRoomUnavailableState(roomUnavailable);
//     }
//   }, [rooms]);


//   //. Calendar tile class
//   // const tileClassName = (roomId, date) => {
//   //   const bookings = roomAvailability[roomId] || [];
//   //   const isoDate = date.toLocaleDateString("en-CA");
//   //   const day = bookings.find((b) => b.date === isoDate);
//   //   if (!day) return "";
//   //   if (day.status === "booked") return "calendar-booked";
//   //   if (day.status === "maintenance") return "calendar-maintenance";
//   //   return "calendar-available";
//   // };

//   const scrollToRooms = () => {
//     roomsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
//   };

//   const handleBookNow = async (room) => {
//     try {
//       if (!stayData || !room) {
//         toast.error("Invalid room or stay information.");
//         return;
//       }

//       // Default check-in/out dates
//       const today = new Date();
//       const defaultCheckIn = today.toLocaleDateString("en-CA");
//       const oneWeekLater = new Date();
//       oneWeekLater.setDate(today.getDate() + 7);
//       const defaultCheckOut = oneWeekLater.toLocaleDateString("en-CA");

//       // Selected date range for this room
//       const range = selectedDates?.[room.id] || [];
//       const checkIn = range[0] ? new Date(range[0]).toLocaleDateString("en-CA") : defaultCheckIn;
//       const checkOut = range[1] ? new Date(range[1]).toLocaleDateString("en-CA") : defaultCheckOut;

//       const payload = {
//         stay_id: stayData.id,
//         room_id: room.id,
//         check_in: checkIn,
//         check_out: checkOut,
//         room_count: 1,
//         adults: 2,
//         children: 1,
//       };
//       console.log("Booking payload:", payload);

//       const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
//       const token = storedUser.accessToken || "";

//       const response = await axios.post(
//         `${API_BASE_URL}bookings/review`,
//         payload,
//         { headers: { Authorization: `Bearer ${token}` } }
//       );

//       // Check if the backend says the booking is successful
//       if (response?.data?.success) {
//         localStorage.setItem(
//           "draftBooking",
//           JSON.stringify({
//             success: true,
//             stay: stayData,
//             room,
//             summary: response.data.summary,
//             checkIn,
//             checkOut,
//           })
//         );
//         navigate("/booking/review");
//       } else {
//         // Show the backend error message
//         toast.error(response?.data?.message || "Booking failed. Please check your details.");
//         console.warn("Booking failed:", response?.data?.message);
//       }
//     } catch (err) {
//       console.error("Error creating booking review:", err);
//       toast.error("Network error. Please try again.");
//     }
//   };



//   const hasRooms = Array.isArray(rooms) && rooms.length > 0;

//   return (
//     <div className="row" ref={roomsRef}>
//       <div className="col-12">
//         {!hasRooms ? (
//           <h3 style={{ fontSize: "20px", color: "red" }}>No rooms available.</h3>
//         ) : (
//           <div className="preferredouter">
//             <h3 ref={headingRef}>Please select your preferred room</h3>
//             {rooms.map((room, idx) => {
//               const roomId = room.id || room.title || idx;
//               const selectedRange = selectedDates[roomId] || [];

//               return (
//                 <div key={roomId} className="preferredbx">
//                   {/* Left side */}
//                   <div className="preferredlt">
//                     <div className="preferredltinner">
//                       <h4>{room.title}</h4>
//                       <div className="preferredimgbx">
//                         {room.rating && (
//                           <span className="ratingx">{room.rating} / 10</span>
//                         )}
//                         <img
//                           src={room.cover_image || room.cover_image_url}
//                           alt={room.title}
//                           style={{ width: "100%", height: "auto" }}
//                         />
//                         {Array.isArray(room.images) && room.images.length > 0 && (
//                           <div
//                             style={{
//                               display: "flex",
//                               gap: "8px",
//                               marginTop: "8px",
//                             }}
//                           >
//                             {room.images.slice(0, 3).map((img, index) => (
//                               <div
//                                 key={img.id || index}
//                                 style={{
//                                   width: "30%",
//                                 }}
//                               >
//                                 <img
//                                   src={img.image || img.image_url}
//                                   alt={`Room ${room.title} ${index + 1}`}
//                                   style={{
//                                     width: "100%",
//                                     height: "80px",
//                                     objectFit: "cover",
//                                     borderRadius: "4px",
//                                   }}
//                                 />
//                               </div>
//                             ))}
//                           </div>
//                         )}


//                       </div>
//                       <div className="ammenties">
//                         {room.beds > 0 && (
//                           <span>
//                             <i className="icon icon-bed"></i>
//                             <small>{room.beds} {room.beds > 1 ? "Beds" : "Bed"}</small>
//                           </span>
//                         )}

//                         {/* {room.details?.[0] && (
//                           <span>
//                             <i className="icon icon-room"></i>
//                             <small>{room.details[0] || ""}</small>
//                           </span>
//                         )} */}

//                         {room.size && (
//                           <span>
//                             <i className="icon icon-feet"></i>
//                             <small>{room.size}</small>
//                           </span>
//                         )}

//                         {room.bathrooms > 0 && (
//                           <span>
//                             <i className="icon icon-bathroom"></i>
//                             <small>
//                               {room.bathrooms} {room.bathrooms > 1 ? "Bathrooms" : "Bathroom"}
//                             </small>
//                           </span>
//                         )}
//                       </div>

//                     </div>
//                   </div>

//                   {/* Right side */}
//                   <div className="preferredrt">
//                     <div className="preferredtop">
//                       {/* <div className="availability-calendar">
//                         <Calendar
//                           minDate={new Date()}
//                           maxDate={new Date(
//                             new Date().setFullYear(new Date().getFullYear() + 1)
//                           )}
//                           maxDetail="month"
//                           minDetail="month"
//                           onActiveStartDateChange={({ activeStartDate }) => {
//                             const maxAllowed = new Date();
//                             maxAllowed.setFullYear(maxAllowed.getFullYear() + 1);
//                             if (activeStartDate > maxAllowed) {
//                               return false;
//                             }
//                           }}
//                           selectRange={false}
//                           onClickDay={(date) => {
                        
//                             const iso = date.toLocaleDateString("en-CA");
//                             const day = (roomAvailability[roomId] || []).find(
//                               (b) => b.date === iso
//                             );

//                             if (!day || day.status !== "available") {
//                               setMessage("Selected date is not available.");
//                               return;
//                             }

//                             if (!tempStartDate) {
//                               setTempStartDate(date);
//                               setSelectedDates((prev) => ({
//                                 ...prev,
//                                 [roomId]: [date],
//                               }));
//                               setMessage(
//                                 "Check-in date selected. Now select your check-out date."
//                               );
//                               return;
//                             }

//                             if (tempStartDate && date > tempStartDate) {
//                               const temp = [];
//                               let current = new Date(tempStartDate);
//                               while (current <= date) {
//                                 const isoDate =
//                                   current.toLocaleDateString("en-CA");
//                                 const dayStatus = (
//                                   roomAvailability[roomId] || []
//                                 ).find((b) => b.date === isoDate);
//                                 if (!dayStatus || dayStatus.status !== "available") {
//                                   setMessage(
//                                     "Some dates in this range are not available."
//                                   );
//                                   return;
//                                 }
//                                 temp.push(isoDate);
//                                 current.setDate(current.getDate() + 1);
//                               }

//                               setSelectedDates((prev) => ({
//                                 ...prev,
//                                 [roomId]: [tempStartDate, date],
//                               }));
//                               setTempStartDate(null);
//                               setMessage("");
//                               const payload = {
//                                 ...JSON.parse(
//                                   localStorage.getItem("searchParams") || "{}"
//                                 ),
//                                 checkin_date:
//                                   tempStartDate.toLocaleDateString("en-CA"),
//                                 checkout_date: date.toLocaleDateString("en-CA"),
//                               };
//                               localStorage.setItem(
//                                 "searchParams",
//                                 JSON.stringify(payload)
//                               );
//                             } else {
//                               setMessage("Check-out date must be after check-in.");
//                             }
//                           }}
//                           tileContent={({ date, view }) => {
//   if (view !== "month") return null;

// const iso = date.toLocaleDateString("en-CA");
//   const roomDates = roomAvailability[roomId] || [];
//   const day = roomDates.find((b) => b.date === iso);

//   if (day?.status === "available") {
//     return <div className="calendar-flag available-flag">A</div>;
//   }
//   if (day?.status === "booked") {
//     return <div className="calendar-flag booked-flag">X</div>;
//   }
//   return null;
// }}

//                           tileClassName={({ date, view }) => {
//                             if (view !== "month") return null;

//                             const iso = date.toLocaleDateString("en-CA");
//                             const roomDates = roomAvailability[roomId] || [];
//                             const day = roomDates.find((b) => b.date === iso);

//                             if (!day) return "";

//                             if (day.status === "booked") return "calendar-booked";
//                             if (day.status === "maintenance")
//                               return "calendar-maintenance";

//                             const range = selectedDates[roomId] || [];
//                             const params = JSON.parse(
//                               localStorage.getItem("searchParams") || "{}"
//                             );

//                             const start = parseLocalDate(params.checkin_date);
//                             const end = parseLocalDate(params.checkout_date);

//                             if (start && end && date >= start && date <= end)
//                               return "calendar-in-range";

//                             if (
//                               range.length === 2 &&
//                               date >= range[0] &&
//                               date <= range[1]
//                             )
//                               return "calendar-in-range";

//                             if (
//                               tempStartDate &&
//                               date.getTime() === tempStartDate.getTime()
//                             )
//                               return "calendar-in-range";

//                             if (day.status === "available")
//                               return "calendar-available";

//                             return "";
//                           }}
//                         />
 
//                         <div className="calendar-legend">
//                           <div className="legend-item">
//                             <span className="legend-box available"></span> Available
//                           </div>
//                           <div className="legend-item">
//                             <span className="legend-box booked"></span> Booked
//                           </div>
//                         </div>
//                         {selectedRange.length === 2 && (
//                           <div className="mt-2 availablebtx">
//                             Selected:{" "}
//                             {selectedRange[0].toLocaleDateString()} →{" "}
//                             {selectedRange[1].toLocaleDateString()}
//                           </div>
//                         )}
//                         {message && (
//                           <div className="mt-2 infobtx">{message}</div>
//                         )}
//                       </div> */}
//                     </div>

//                     <div className="preferredbtm">
//                       <div className="row">
//                         <div className="col-lg-4 order-lg-2">
//                           <div className="pricebtm">
//                             <span className="price">₹ {room.price_min}</span>
//                             {/* {room.tax_per_night && (
//                               <span className="taxtext">
//                                 + ₹ {room.tax_per_night} taxes & fees Per Night
//                               </span>
//                             )} */}
//                             <span className="taxtext">
//                               + Taxes & Fees Will be applied.
//                             </span>
//                             {/* <button
//                               className="theme-btn"
//                               type="button"
//                               onClick={() => handleBookNow(room)}
//                               disabled={!selectedRange.length}
//                             >
//                               {selectedRange.length
//                                 ? "Book Now"
//                                 : "Select Dates"}
//                             </button> */}
//                             <div className="room-action">
//                               {roomUnavailableState[roomId] && (
//                                 <div className="infotext w-100 mt-2 mb-1">
//                                   <i className="icon icon-info"></i>
//                                   <span className="mb-0">The room is currently unavailable for your selected date. We apologize for the inconvenience — please try another date or room option.</span>
//                                 </div>
//                               )}

//                               <button
//                                 className={`theme-btn ${roomUnavailableState[roomId] ? "disabled" : ""}`}
//                                 type="button"
//                                 onClick={() => handleBookNow(room)}
//                                 disabled={roomUnavailableState[roomId] || !selectedRange.length}
//                               >
//                                 Book Now
//                               </button>
//                             </div>




//                           </div>
//                         </div>
//                         <div className="col-lg-8">
//                           <ul>
//                             {(() => {
//                               let benefits = [];

//                               try {
//                                 benefits = Array.isArray(room.benefits)
//                                   ? room.benefits
//                                   : JSON.parse(room.benefits || "[]");
//                               } catch {
//                                 benefits = [];
//                               }

//                               return benefits.length > 0 ? (
//                                 benefits.map((b, idx) => <li key={idx}>{b}</li>)
//                               ) : (
//                                 <li>No benefits listed</li>
//                               );
//                             })()}
//                           </ul>

//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               );
//             })}
//           </div>
//         )}
//       </div>

//       {/*. Calendar flag styles */}
//       <style jsx global>{`

// //       .react-calendar__tile {
// //   position: relative; /* important for absolute child positioning */
// // }

// //         .calendar-flag {
// //           font-size: 10px;
// //           font-weight: bold;
// //           color: white;
// //           background: gray;
// //           text-align: center;
// //           width: 18px;
// //           height: 18px;
// //           border-radius: 50%;
// //           line-height: 18px;
// //           position: absolute;
// //           bottom: 3px;
// //           right: 3px;
// //         }
// //         .available-flag {
// //           background: green;
// //         }
// //         .booked-flag {
// //           background: red;
// //         }
//       `}</style>
//     </div>
//   );
// };
// export default PropertyRooms;